type RequestStatus =
  | 'pending'
  | 'inflight'
  | 'headersReceived'
  | 'complete'
  | 'failed';

interface TTSRequest {
  status: RequestStatus;
  start(): void;
  on(
    event: 'headersReceived' | 'error' | 'complete',
    cb: (...args: any[]) => void
  ): void;
}

/**
 * TTSRequestManager handles the queueing of tts requests in order to prevent overloading of the
 * browser's simultaneous connection limit. We still process a fixed number of requests in parallel
 * to remain efficient. An enqueued request will begin when a previous request completes or errors.
 *
 * Max parallel persistent connections:
 *  Chrome:     6
 *  Firefox:    6
 *  Safari 5:   6
 *  IE 10:      8
 *
 * @class TTSRequestManager
 */
class TTSRequestManager {
  /**
   * Maximum number of concurrent active streams (requests with open HTTP connections). This value
   * allows us to balance the browser connection limits as well as the load on the TTS service.
   */
  public static CONNECTION_LIMIT = 3;

  private pool: Array<TTSRequest> = [];

  private inflightRequests: number = 0;

  public addRequest(request: TTSRequest) {
    this.pool.push(request);
    this.poolHandler();
  }

  /**
   * Process the next item in the request pool.
   */
  private poolHandler() {
    if (!this.pool.length) {
      return;
    }
    if (this.inflightRequests >= TTSRequestManager.CONNECTION_LIMIT) {
      return;
    }
    const request = this.getNextPendingRequest();
    if (!request) {
      return;
    }
    let noLongerInflight = false;
    request.on('headersReceived', () => {
      // Headers received - stream is active but still inflight until complete/error
      // Don't decrement inflightRequests here, allow next request to start only after stream completes
    });
    request.on('error', () => {
      if (noLongerInflight === false) {
        noLongerInflight = true;
        this.inflightRequests -= 1;
        this.poolHandler();
      }
      this.removeRequestFromPool(request);
    });
    request.on('complete', () => {
      if (noLongerInflight === false) {
        noLongerInflight = true;
        this.inflightRequests -= 1;
        this.poolHandler();
      }
      this.removeRequestFromPool(request);
    });
    this.inflightRequests += 1;
    request.start();
  }

  private getNextPendingRequest(): TTSRequest | undefined {
    return this.pool.find(request => request.status === 'pending');
  }

  private removeRequestFromPool(request: TTSRequest) {
    this.pool = this.pool.filter(req => req !== request);
  }
}

export default new TTSRequestManager();
export { TTSRequestManager };
export type { TTSRequest, RequestStatus };
